import { useEffect } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeStandardSearchEvent } from '../../common/AdobeTagging/adobeStandardSearchEvent';
import {
  convertProviderTypeToAdobeType,
  getSearchLocation,
} from '../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { useProviderSearchTelemetry } from '../../hooks/useProviderSearchTelemetry';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../store/useAnalyticsStore/analyticsStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { CommonlySearchedCard } from '../CommonlySearched';
import {
  Constants,
  ReverseCoverageTypesCodes,
  SUGGESTED_PROVIDER,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { SuggestedProviders } from '../SuggestedProviders/SuggestedProviders';
import { WhereToGetHelp } from '../WhereToGetHelp/WhereToGetHelp';
import { NoResult } from './NoResult';
import {
  CommonlySearchedStyled,
  NullPageContainerStyled,
  TopRecommendedStyled,
  WhereToGetHelpContainer,
} from './NullResultsPage.styles';
import { TopRecommendedProvider } from './TopRecommendedProvider';

export type NullResultsPageProps = {
  searchTitle: string;
  specialtyCode: string;
  emptyResults: boolean;
  specialtySearch?: boolean;
  onCommonSearchChipClicked: () => void;
};

export const NullResultsPage = ({
  searchTitle,
  specialtyCode,
  emptyResults,
  specialtySearch,
  onCommonSearchChipClicked,
}: NullResultsPageProps) => {
  const coverageType = useCoverageType();
  const { searchMethod, search, searchTerm, providerType, searchType } =
    useTypeaheadStore(
      useShallow((state: TypeaheadState) => ({
        searchMethod: state.typeaheadSearchStore.searchMethod,
        search: state.typeaheadSearchStore.search,
        searchTerm: state.typeaheadSearchStore.searchTerm,
        providerType: state.typeaheadSearchStore.providerType,
        searchType: state.typeaheadSearchStore.searchType,
      }))
    );
  const { originLinkNameForAnalytics } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      originLinkNameForAnalytics:
        state.analyticsState.originLinkNameForAnalytics,
    }))
  );
  const storedLocation = useGeoLocationStorage();
  const lagoonData = useLagoon('null-search-page-common')();
  const IsCommonNullPage = searchMethod === 'typed';

  const requiredCoveragetype = IsCommonNullPage ? 'COMMON' : coverageType;

  const filteredLagoonData = lagoonData?.find(
    ({ coverageType: lagoonCoverageType }) =>
      lagoonCoverageType?.split(',').includes(requiredCoveragetype)
  );
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: 'null results',
    sitesectionLevel1: 'search results',
    sitesectionLevel2: 'category',
  });

  useEffect(() => {
    adobePageTrackEvent();

    const method = searchMethod ?? 'guided';
    const term = method === 'guided' ? '' : searchTerm || search;
    adobeStandardSearchEvent({
      term,
      type: convertProviderTypeToAdobeType(providerType),
      linkName: originLinkNameForAnalytics,
      method,
      numberOfResults: 0,
      customAttributesBlock: {
        providerType: '',
        searchLocation: getSearchLocation(storedLocation),
      },
    });
  }, []);

  const { ALL, BEHAVIORAL_HEALTH, VISION, DENTAL } = Constants.CHIPS_CATEGORIES;
  const getSelectedChipValue = () => {
    if (coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'])
      return BEHAVIORAL_HEALTH;
    if (coverageType === ReverseCoverageTypesCodes.VISION) return VISION;
    if (coverageType === ReverseCoverageTypesCodes.DENTAL) return DENTAL;

    return ALL;
  };

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();
  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );

  const [isSearchRelevancyEventTrackingEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
  ]);

  const hitProviderSearchTelemetry = async () => {
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      timestamp: new Date().toISOString(),
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius ?? 0,
      },
      interpretedAsNameSearch: searchType !== 'speciality',
      viewType: 'other',
      results: [],
      resultPage: 0,
      pageSize: 0,
      resultedInNullSearch: true,
      searchPlatform: 'web',
      event: 'mixed',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      searchTerm &&
      searchTerm?.length > 0 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    }
  }, [searchTerm]);

  return (
    <NullPageContainerStyled>
      <NoResult
        data-auto-testid="null-page-abyss-box-root"
        data-testid="null-page-abyss-box-root"
        lagoonData={filteredLagoonData}
        searchTitle={searchTitle}
        specialtySearch={specialtySearch}
      />
      {specialtyCode === SUGGESTED_PROVIDER ? <SuggestedProviders /> : null}
      {specialtyCode === SUGGESTED_PROVIDER && emptyResults ? (
        <WhereToGetHelpContainer>
          <SuggestedProviders />
        </WhereToGetHelpContainer>
      ) : null}
      {!IsCommonNullPage ? (
        <TopRecommendedStyled>
          <TopRecommendedProvider
            coverageType={coverageType}
            heading={filteredLagoonData?.topProviderHeading}
          />
        </TopRecommendedStyled>
      ) : null}
      <CommonlySearchedStyled>
        <CommonlySearchedCard
          onCommonSearchChipClicked={onCommonSearchChipClicked}
          selectedChipValue={getSelectedChipValue()}
        />
      </CommonlySearchedStyled>
      {coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ? (
        <WhereToGetHelpContainer>
          <WhereToGetHelp selectedChipValue="BEHAVIORAL HEALTH" />
        </WhereToGetHelpContainer>
      ) : null}
    </NullPageContainerStyled>
  );
};
